import { useContext, useState, useEffect } from "react";
import { useSignIn, useSignOut } from "react-auth-kit";
import { useNavigate, useParams } from "react-router-dom";
import {
  createClientIdQueryString,
  sendApiRequest,
} from "../../services/Base/base-service";

import UserContext from "../../store/user-context";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Button,
  Input,
  Select,
  FormGroup,
  Spinner,
  Alert,
  Icon,
} from "design-react-kit";
import DocumentTypeEnum from "../../helpers/Enums/DocumentTypeEnum";
import debounce from "lodash.debounce";

const ClientDocumentsConfigModal = ({
  modalOpen,
  modalCloseHandler,
  editableDocument,
  notifyUser,
  setDocuments,
  setDocumentsNumber,
}) => {
  const [doc, setDocument] = useState(null);
  const [supportedDocumentTypes, setSupportedDocumentTypes] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
  const [selectedDpi, setSelectedDpi] = useState(null);

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const params = useParams();
  const clientId = params?.clientId;
  const clientName = params?.client;

  const selectedMunicipalitiesNames = selectedMunicipalities.map(
    (item) => item.label
  );

  useEffect(() => {
    if (error !== null) {
      const modalBody = document.querySelector(".modal.show .modal-body");
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    }
  }, [error]);

  useEffect(() => {
    if (modalOpen) {
      setSelectedMunicipalities([]);
      setMunicipalities([]);
      setSelectedDpi(null);
      setDocument(null);
      setError(null);
      if (editableDocument === null) {
        getAllCadastralCodesByMunicipalityName(
          userContext.selectedClientName.replace("Comune di ", ""),
          true
        );
        setTimeout(() => {
          sendApiRequest(
            "ClientConfiguration/GetSupportedDocumentTypes",
            "GET",
            true,
            { clientId },
            signIn,
            signOut,
            navigate,
            createClientIdQueryString,
            userContext
          ).then((response) => {
            if (response !== undefined) {
              if (response.errors !== null) {
                setError(response.errors);
                setIsLoading(false);
              } else {
                let options = [];
                response.data.supportedDocumentTypes.forEach((el) => {
                  options.push({
                    value: el.documentType,
                    label: el.documentTypeName,
                    documentTypeName: el.documentTypeName,
                    documentType: el.documentType,
                    clientDocumentRuleId: null,
                    clientId: clientId,
                    minOccurrence: 0,
                    maxOccurrence: 0,
                    isAttached: false,
                    isAnalyzed: false,
                    municipalityCodes: [],
                    municipalityCadasrtalCodes: selectedMunicipalities,
                    ocrExtractionDpi: 300,
                  });
                });
                setSupportedDocumentTypes(options);
                setSelectedDpi({
                  value: 300,
                  label: "300 dpi",
                });
                setIsLoading(false);
              }
            }
          });
        }, 300);
      } else {
        setDocument(editableDocument);
        if (
          editableDocument.documentType === DocumentTypeEnum.F24 &&
          editableDocument.municipalityCadastralCodes !== null
        ) {
          let municipalityCadasrtalCodes = [];
          editableDocument.municipalityCadastralCodes.map((item) =>
            municipalityCadasrtalCodes.push({
              value: item.code,
              label: item.municipalityName,
            })
          );

          setSelectedDpi({
            value: editableDocument.ocrExtractionDpi,
            label: `${editableDocument.ocrExtractionDpi} DPI`,
          });
          setSelectedMunicipalities(municipalityCadasrtalCodes);
        }
      }
    } else {
      setDocument(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, editableDocument]);

  useEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        const check = document.getElementById("isAnalyzed");
        if (doc?.minOccurrence === "0" || doc?.minOccurrence === 0) {
          check.setAttribute("disabled", true);
          check.checked = false;
          changeHandler("isAnalyzed", false);
        } else {
          check.removeAttribute("disabled");
        }
      }, 300);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, doc]);

  const changeHandler = (property, value) => {
    setDocument((curState) => {
      const tempDoc = { ...curState };
      tempDoc[`${property}`] = value;
      return tempDoc;
    });
  };

  const handleSelectedDocumentFieldsChange = (selectedOption) => {
    const newOption = {
      ...selectedOption,
      municipalityCadasrtalCodes: selectedMunicipalities,
    };
    setDocument(newOption);
  };

  const upsertClientDocumentRuleHandler = () => {
    if (
      doc.documentType === DocumentTypeEnum.F24 ||
      doc.documentType === DocumentTypeEnum.PaymentNotice
    ) {
      if (doc.minOccurrence === "0") {
        setError(["Numero minimo documenti non può essere 0!"]);
      } else if (!doc.isAnalyzed) {
        setError(["Il documento di tipo pagamento deve essere analizzato!"]);
      } else {
        upsertClientDocumentRule();
      }
    } else {
      upsertClientDocumentRule();
    }
  };

  const upsertClientDocumentRule = () => {
    const data = {
      ...doc,
      municipalityCodes: selectedMunicipalities.map((item) => item.value),
      ocrExtractionDpi: selectedDpi?.value,
    };

    sendApiRequest(
      `ClientConfiguration/UpsertSingleClientDocumentRule`,
      "POST",
      true,
      data,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
          setIsLoading(false);
        } else {
          modalCloseHandler();
          setIsLoading(false);
          notifyUser(
            doc.clientDocumentRuleId !== null
              ? "Aggiornamento configurazione per documento completato con successo!"
              : "Creazione configurazione per documento completata con successo!",
            "success",
            "it-check"
          );
        }
      }
    });
  };

  const handleMunicipalityChange = (index, selectedOption) => {
    setSelectedMunicipalities((prev) => {
      if (prev.length === 0) {
        return [selectedOption];
      } else {
        const updated = prev.map((item, i) =>
          i === index ? selectedOption : item
        );
        return updated;
      }
    });
  };

  const handleInputChange = debounce(async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setMunicipalities([]);
      return;
    }

    setIsLoading(true);
    try {
      getAllCadastralCodesByMunicipalityName(inputValue, false);
    } catch (error) {
      setError(["Error fetching municipalities:" + error]);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  const getAllCadastralCodesByMunicipalityName = (inputValue, isNewRule) => {
    sendApiRequest(
      `Municipality/GetAllCadastralCodesByMunicipalityName?MunicipalitySearchName=${inputValue}`,
      "GET",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
        } else {
          if (!isNewRule) {
            let municipalityCadasrtalCodes = [];
            response.data.municipalityCadasrtalCodes.map((item) =>
              municipalityCadasrtalCodes.push({
                value: item.code,
                label: item.municipalityName,
              })
            );
            setMunicipalities(municipalityCadasrtalCodes);
          } else {
            let municipality = response.data.municipalityCadasrtalCodes[0];
            if (municipality !== null && municipality !== undefined) {
              setSelectedMunicipalities([
                {
                  value: municipality.code,
                  label: municipality.municipalityName,
                },
              ]);
            }
          }
        }
      }
    });
  };

  const deleteMunicipalityCadastralCode = (municipalityCadastralCode) => {
    setSelectedMunicipalities((curState) => {
      var newState = curState.filter(
        (item) => item.value !== municipalityCadastralCode
      );
      return newState;
    });
  };

  const ocrExtractionDpi = [
    {
      value: 300,
      label: "300 DPI",
    },
    {
      value: 400,
      label: "400 DPI",
    },
  ];

  const handleDpiChange = (selectedOption) => {
    setSelectedDpi(selectedOption);
  };

  return (
    <Modal
      isOpen={modalOpen}
      toggle={modalCloseHandler}
      align="center"
      scrollable
      labelledBy="clientDocConfig"
    >
      <ModalHeader toggle={modalCloseHandler} id="clientDocConfig">
        <h5
          style={{ fontSize: "28px", textTransform: "capitalize" }}
          className="mb-4"
        >
          {editableDocument !== null &&
            editableDocument !== undefined &&
            "Configura Documento"}{" "}
          {(editableDocument === null || editableDocument === undefined) &&
            "Aggiungi Documento"}{" "}
        </h5>
        <span>
          Tipologia servizio:{" "}
          <span style={{ color: "#0073e6" }}>{clientName}</span>
        </span>
        {editableDocument !== null &&
          editableDocument !== undefined &&
          doc !== null &&
          doc !== undefined &&
          doc.documentTypeName !== undefined &&
          doc.documentTypeName !== null && (
            <div>
              Documento:{" "}
              <span style={{ color: "#0073e6" }}>{doc?.documentTypeName}</span>
            </div>
          )}
      </ModalHeader>
      <ModalBody className="col-12">
        {isLoading && <Spinner double active />}
        {!isLoading && error !== null && error.length !== undefined && (
          <Alert color="danger">
            {error.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </Alert>
        )}
        {(editableDocument === null || editableDocument === undefined) && (
          <div className="bootstrap-select-wrapper mt-5">
            <label htmlFor="documents">Seleziona Documento:</label>
            <Select
              id="documents"
              isSearchable={true}
              onChange={handleSelectedDocumentFieldsChange}
              options={supportedDocumentTypes}
              value={doc}
              placeholder="-Seleziona-"
              aria-label="Seleziona"
            />
          </div>
        )}
        <Input
          id="minDocuments"
          type="number"
          min={0}
          placeholder="Numero minimo documenti"
          label="Numero minimo documenti"
          className="mt-5 mb-5"
          value={doc?.minOccurrence}
          onChange={(e) => {
            changeHandler("minOccurrence", e.target.value);
          }}
        />
        <Input
          id="maxDocuments"
          type="number"
          min={0}
          placeholder="Numero massimo documenti"
          label="Numero massimo documenti"
          className="mb-5"
          value={doc?.maxOccurrence}
          onChange={(e) => {
            changeHandler("maxOccurrence", e.target.value);
          }}
        />
        <FormGroup className="col-sm-12 m-0">
          <Input
            className="mb-5"
            id="documentNamePattern"
            type="text"
            label="Document name pattern"
            placeholder="Inserisci document name pattern"
            value={doc?.documentNamePattern}
            onChange={(e) =>
              changeHandler("documentNamePattern", e.target.value)
            }
          />
        </FormGroup>

        {((editableDocument !== null &&
          editableDocument !== undefined &&
          editableDocument.documentType === DocumentTypeEnum.F24) ||
          (doc !== null &&
            doc !== undefined &&
            doc.documentType === DocumentTypeEnum.F24)) && (
          <>
            {selectedMunicipalities.length === 0 && (
              <>
                <hr className="mx-auto mb-5" />
                <div className="d-flex align-items-center justify-content-between">
                  <div className="bootstrap-select-wrapper mb-2 col-sm-7">
                    <label htmlFor="municipalities">
                      Comune ammesso in F24
                    </label>
                    <Select
                      isSearchable={true}
                      isLoading={isLoading}
                      id="municipalities"
                      placeholder="Selezione il comune"
                      aria-label="Seleziona"
                      value={selectedMunicipalities}
                      onChange={(option) => handleMunicipalityChange(0, option)}
                      onInputChange={handleInputChange}
                      options={municipalities.filter(
                        (opt) =>
                          !selectedMunicipalitiesNames.includes(opt.label)
                      )}
                    />
                  </div>
                  <div className="bootstrap-select-wrapper mb-4 col-sm-4">
                    <label htmlFor="cadastralCode">Codice catastale</label>
                    <p className="w-100 border-bottom border-black pl-2 mb-4 pb-2 mt-4">
                      {selectedMunicipalities?.value}
                    </p>
                  </div>
                </div>
                <p className="cadastralCodeInfo">
                  <Icon icon="it-warning-circle" color="warning" />
                  Devi inserire almeno 3 caratteri per effettuare la ricerca.
                </p>
              </>
            )}
            {selectedMunicipalities.length > 0 &&
              selectedMunicipalities.map((municipality, index) => {
                return (
                  <div key={index}>
                    <hr className="mx-auto mb-4 mt-1" />

                    <div className="d-flex align-items-center justify-content-between">
                      <div className="bootstrap-select-wrapper mb-2 col-sm-7">
                        <label htmlFor="municipalities">
                          Comune ammesso in F24
                        </label>
                        <Select
                          isSearchable={true}
                          isLoading={isLoading}
                          id="municipalities"
                          placeholder="Selezione il comune"
                          aria-label="Seleziona"
                          value={municipality}
                          onChange={(option) =>
                            handleMunicipalityChange(index, option)
                          }
                          onInputChange={handleInputChange}
                          options={municipalities.filter(
                            (opt) =>
                              !selectedMunicipalitiesNames.includes(opt.label)
                          )}
                        />
                      </div>
                      <div className="bootstrap-select-wrapper mb-1 col-sm-4">
                        <label htmlFor="cadastralCode">Codice catastale</label>
                        <p className="w-100 border-bottom border-black pl-2 mt-4">
                          {municipality?.value}
                        </p>
                      </div>
                      <div className="bootstrap-select-wrapper mb-2 d-flex justify-content-center align-items-center">
                        <span title="Rimuovi codice cadastale">
                          <Icon
                            id="remove-cadastral-code"
                            icon="it-delete"
                            fill="#005A9A"
                            role="button"
                            onClick={(e) =>
                              deleteMunicipalityCadastralCode(
                                municipality.value
                              )
                            }
                          />
                        </span>
                      </div>
                    </div>
                    <p className="cadastralCodeInfo">
                      <Icon icon="it-warning-circle" color="warning" />
                      Devi inserire almeno 3 caratteri per effettuare la
                      ricerca.
                    </p>
                  </div>
                );
              })}

            <div className="bootstrap-select-wrapper mb-2 d-flex justify-content-center align-items-center">
              <span title="Aggiungi nuovo codice cadastale">
                <Icon
                  id="add-cadastral-code"
                  icon="it-plus-circle"
                  fill="#005A9A"
                  role="button"
                  onClick={(e) =>
                    setSelectedMunicipalities((prevValue) => [
                      ...prevValue,
                      {
                        value: "",
                        label: "",
                      },
                    ])
                  }
                />
              </span>
            </div>
            <hr className="mx-auto" />
            <div className="bootstrap-select-wrapper mb-5 mt-5 col-sm-7">
              <label htmlFor="municipalities">Tesseract (OCR): DPI</label>
              <Select
                isSearchable={true}
                isLoading={isLoading}
                id="municipalities"
                placeholder="Selezione il comune"
                aria-label="Seleziona"
                value={selectedDpi}
                onChange={handleDpiChange}
                options={ocrExtractionDpi}
              />
            </div>
          </>
        )}

        <div className="form-group mb-2">
          <input
            type="checkbox"
            id={`isAttachable`}
            className="mt-3"
            checked={doc?.isAttached}
            onChange={(e) => {
              changeHandler("isAttached", e.target.checked);
            }}
          />
          <label htmlFor={`isAttachable`} className="ml-3 checkbox-label">
            Da allegare
          </label>
        </div>
        <div className="form-group">
          <input
            type="checkbox"
            id={`isAnalyzed`}
            className="mt-3"
            checked={doc?.isAnalyzed}
            onChange={(e) => {
              changeHandler("isAnalyzed", e.target.checked);
            }}
          />
          <label htmlFor={`isAnalyzed`} className="ml-3 checkbox-label">
            Da analizzare
          </label>
        </div>
        <div className="col-sm-12 mb-2 d-flex align-items-center justify-content-around">
          <Button
            color="white"
            className="mr-2"
            onClick={modalCloseHandler}
            id="config-cancel"
          >
            Annulla
          </Button>
          <Button
            id="config-save"
            style={{
              backgroundColor: "#5EB423",
              color: "#fff",
              padding: "7px 15px",
            }}
            onClick={(e) => upsertClientDocumentRuleHandler()}
          >
            Salva
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ClientDocumentsConfigModal;
